import React, { useRef, Dispatch, SetStateAction } from 'react';

import {
  Drawer,
  Typography,
  IconButton,
  MuiThemeProvider,
  makeStyles
} from '@material-ui/core';
import { Form, FormValues, yupSchemaToValidationFunc } from 'components/Forms';
import { SecondaryButton, SubmitButton } from 'components/Forms/Buttons';
import { feedbackButtonZIndex } from 'styles/defaultTheme';
import { FormApi } from 'final-form';
import { useAllJobTypesData } from 'store/selectors';
import CloseIcon from '@material-ui/icons/Close';
import {
  addCapacityType,
  fetchCapacityTypes
} from 'store/slices/capacityTypes';
import { useDispatch } from 'react-redux';
import { AutocompleteFilterOptions } from 'components/FilterSearch/FilterSearch';
import { setSnackbar, SnackbarStatus } from 'store/slices/snackbar';
import reserveCapacityTheme from 'views/ReserveCapacity/reserveCapacityTheme';
import {
  AddCapacityTypeFormFields,
  getAddCapacityTypeFields,
  validationSchema
} from './addCapacityFormConfig';

const useDrawerStyles = makeStyles(
  theme => ({
    drawer: {
      width: theme.containers.small,
      padding: theme.spacing(4, 3, 2, 3)
    },
    header: {
      display: 'flex',
      paddingBottom: theme.spacing(4),
      alignItems: 'flex-start',
      justifyContent: 'space-between'
    },
    closeButton: {
      '& .MuiSvgIcon-root': {
        fontSize: theme.spacing(3),
        cursor: 'pointer'
      },
      padding: 0
    },
    submitButton: {
      marginLeft: theme.spacing(2)
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      paddingTop: theme.spacing(3)
    }
  }),
  { name: 'AddCapacityTypeDrawer' }
);

interface AddCapacityTypeDrawerProps {
  isOpen: boolean;
  toggleDrawer: () => void;
  setCreatedId: Dispatch<SetStateAction<string | undefined>>;
}

export default function AddCapacityTypeDrawer({
  isOpen,
  toggleDrawer,
  setCreatedId
}: AddCapacityTypeDrawerProps) {
  const drawerClasses = useDrawerStyles();
  const formRef = useRef<FormApi>();
  const dispatch = useDispatch();

  const submitForm = async (values: FormValues) => {
    const createdId = await dispatch<Promise<string>>(
      addCapacityType({
        name: values[AddCapacityTypeFormFields.CapacityName],
        jobTypes: (values[
          AddCapacityTypeFormFields.JobType
        ] as AutocompleteFilterOptions[])?.map(({ value }) => value),
        notes: values[AddCapacityTypeFormFields.Notes]
      })
    );
    dispatch(fetchCapacityTypes());
    setCreatedId(createdId);
    toggleDrawer();
  };

  const handleDrawerClose = () => {
    const formState = formRef.current?.getState();

    if (formState?.dirty) {
      dispatch(
        setSnackbar({
          message: 'Your changes have been discarded.',
          show: true,
          status: SnackbarStatus.Success,
          useNewSnackbar: true
        })
      );
    }
    toggleDrawer();
  };

  const handleDrawerSubmit = () => formRef.current?.submit();

  const addCapacityTypeFields = getAddCapacityTypeFields(useAllJobTypesData());

  return (
    <Drawer
      variant="temporary"
      anchor="right"
      open={isOpen}
      onClose={handleDrawerClose}
      classes={{
        paper: drawerClasses.drawer
      }}
      ModalProps={{
        style: {
          // This makes sure that the drawer is on top of the "Feedback" button.
          zIndex: feedbackButtonZIndex
        }
      }}
      data-testid="addCapacityTypeDrawer"
    >
      <header className={drawerClasses.header}>
        <Typography variant="h1">Add capacity type</Typography>
        <IconButton
          aria-label="close"
          onClick={handleDrawerClose}
          className={drawerClasses.closeButton}
          size="medium"
        >
          <CloseIcon fontSize="medium" />
        </IconButton>
      </header>
      <MuiThemeProvider theme={reserveCapacityTheme}>
        <Form
          validationFunc={yupSchemaToValidationFunc(validationSchema)}
          fields={addCapacityTypeFields}
          onSubmit={submitForm}
          goBackToPreviousPage={false}
        >
          {({ form }) => {
            formRef.current = form;
          }}
        </Form>
      </MuiThemeProvider>
      <footer className={drawerClasses.footer}>
        <SecondaryButton
          size="medium"
          onClick={handleDrawerClose}
          testId="addCapacityTypeCancel"
        >
          Cancel
        </SecondaryButton>
        <SubmitButton
          size="medium"
          onClick={handleDrawerSubmit}
          testId="addCapacityTypeSubmit"
          className={drawerClasses.submitButton}
        >
          Add capacity type
        </SubmitButton>
      </footer>
    </Drawer>
  );
}
